import parseFrontmatter, { type FrontMatterResult } from 'front-matter';

import { createHTTPError } from '@gitbook/util-debug';

export enum FrontmatterParsingState {
    PARSED,
    POTENTIAL,
    NONE,
}

export type FrontmatterRawAttributes = {
    [key: string]: string | number | object | undefined;
};

const frontmatterStartRegex = /^(\uFEFF)?---([^\S]|$)/;

/** Line closing a frontmatter block, as accepted by the `front-matter` package. */
const frontmatterEndRegex = /^(?:---|\.\.\.)\s*$/;

/**
 * A frontmatter that is not valid YAML.
 *
 * Exposed to the caller: only the document's author can fix it, and no retry ever will.
 */
export class InvalidFrontmatterError extends createHTTPError.BadRequest {
    constructor(reason: string, options?: ErrorOptions) {
        super(`Invalid frontmatter: ${reason}`, undefined, options);
    }
}

/**
 * Parse the frontmatter of a markdown input.
 *
 * Wraps the YAML parser so a malformed frontmatter — an unquoted value containing `: ` is the
 * common one — surfaces as an {@link InvalidFrontmatterError} instead of leaking a raw
 * `YAMLException` to the caller as an internal error.
 */
export function parseFrontmatterOrThrow<T>(input: string): FrontMatterResult<T> {
    try {
        return parseFrontmatter<T>(input);
    } catch (error) {
        throw new InvalidFrontmatterError(error instanceof Error ? error.message : String(error), {
            cause: error,
        });
    }
}

/**
 * Remove the frontmatter block from a markdown input, leaving the body untouched.
 *
 * Lets a caller salvage a document whose frontmatter cannot be parsed: its metadata is lost,
 * but the content is not.
 */
export function stripFrontmatter(input: string): string {
    if (!parseFrontmatter.test(input)) {
        return input;
    }

    const lines = input.split('\n');
    const endIndex = lines.findIndex((line, index) => index > 0 && frontmatterEndRegex.test(line));
    return endIndex === -1 ? input : lines.slice(endIndex + 1).join('\n');
}

/**
 * Evaluate the potential state of an incomplete frontmatter.
 */
export function getFrontmatterParsingState(buffer: string): FrontmatterParsingState {
    if (buffer === '' || buffer === '-' || buffer === '--') {
        return FrontmatterParsingState.POTENTIAL;
    }

    const match = buffer.match(frontmatterStartRegex);
    if (!match) {
        return FrontmatterParsingState.NONE;
    }

    if (parseFrontmatter.test(buffer)) {
        return FrontmatterParsingState.PARSED;
    }

    return FrontmatterParsingState.POTENTIAL;
}
